// Shared vocabulary for the configuration panel: what counts as a section, and
// when a section still needs the user's attention. Used by every screen that
// edits an integration's action configurations (connection flow map, destination
// page/modal, new-connection wizard) so the badges mean the same thing everywhere.

// The least an integration record has to look like to be edited here.
export type ConfigTarget = {
    id: string;
    actions: any[];            // integration.type.actions (schema + ui_schema)
    configurations: any[];     // integration.configurations
};

export const isFlowAction = (a: any) => a.type === "pull" || a.type === "push";
export const isAuth = (a: any) => a.type === "auth" || a.value === "auth";
export const hasFields = (a: any) => a.schema?.properties && Object.keys(a.schema.properties).length > 0;
export const slugOf = (a: any) => a.value || a.id;

export const isBlank = (v: any) =>
    v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

export const configFor = (target: ConfigTarget | undefined, action: any) =>
    target?.configurations?.find((c: any) => c.action?.id === action?.id);

// Which of an action's required fields have no value yet. A saved configuration
// isn't proof of setup — actions are often created with defaults that leave the
// required fields empty, so look at the data itself.
export const missingRequiredFields = (target: ConfigTarget, a: any): string[] => {
    if (!hasFields(a)) return [];
    const required: string[] = a.schema?.required ?? [];
    if (required.length === 0) return [];
    const data = configFor(target, a)?.data ?? {};
    return required.filter((f) => isBlank(data[f]));
};

// Drives the "!" badge on nav items, nodes and arrows — keep the surfaces in sync
// by going through this one predicate.
export const actionNeedsSetup = (target: ConfigTarget, a: any) => missingRequiredFields(target, a).length > 0;

// No configuration record at all: whatever the form shows is schema defaults that
// have never been sent to Gundi.
export const actionIsUnsaved = (target: ConfigTarget, a: any) => !configFor(target, a);
